/**
 * Module dependencies.
 */

import { SubTitle } from 'components/atoms/sub-title/sub-title';
import { Typography } from '@mui/material';
import { color } from 'styles/utils/colors';
import { css } from '@emotion/react';
import { ifProp } from 'styled-tools';
import Link from 'next/link';
import styled from '@emotion/styled';

/**
 * Export `FooterContainer` styled component.
 */

export const FooterContainer = styled.footer`
  background-color: #111827;
  color: white;
  padding: 64px 0 32px;
  margin-top: 64px;

  @media (max-width: 480px) {
    padding: 32px 0 32px;
  }
`;

/**
 * `LogoWrapper` styled component.
 */

export const LogoWrapper = styled.div`
  width: 160px;
  align-items: center;
  margin-top: -14px;
  margin-bottom: 16px;
  display: flex;
`;

/**
 * Export `Logo` styled component.
 */

export const Logo = styled.span`
  color: ${color('gray200')};
  height: auto;
  max-width: 300px;
  width: 100%;
`;

/**
 * Export `FooterContent` styled component.
 */

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  display: grid;
  grid-template-columns: 1fr 0.7fr 1.3fr;
  grid-template-areas: 'company quickLinks newsletter';
  gap: 64px;

  @media (max-width: 768px) {
    gap: 16px;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'company newsletter'
      'quickLinks quickLinks';
  }

  @media (max-width: 576px) {
    gap: 16px;
    grid-template-columns: 0.5fr 1fr 1fr 0.5fr;
    grid-template-areas:
      'company company quickLinks quickLinks'
      '. newsletter newsletter .';
  }

  @media (max-width: 480px) {
    gap: 16px;
    grid-template-columns: 1fr;
    grid-template-areas:
      'company'
      'quickLinks'
      'newsletter';
  }
`;

/**
 * Export `FooterSection` styled component.
 */

export const FooterSection = styled.div``;

/**
 * Export `FooterCompanySection` styled component.
 */

export const FooterCompanySection = styled(FooterSection)`
  grid-area: company;
`;

/**
 * Export `FooterQuickLinksSection` styled component.
 */

export const FooterQuickLinksSection = styled(FooterSection)`
  grid-area: quickLinks;

  @media (max-width: 576px) {
    padding-left: 40px;
  }

  @media (max-width: 480px) {
    padding: 32px 0;
  }
`;

/**
 * Export `FooterNewsletterSection` styled component.
 */

export const FooterNewsletterSection = styled(FooterSection)`
  grid-area: newsletter;
`;

/**
 * Export `Title` styled component.
 */

export const Title = styled.h3`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 16px;
`;

/**
 * Export `Paragraph` styled component.
 */

export const Paragraph = styled(Typography)<{
  trim?: boolean;
}>`
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 16px;

  ${ifProp(
    'trim',
    css`
      margin-bottom: 0;
    `,
  )}
`;

/**
 * Export `SocialIcons` styled component.
 */

export const SocialIcons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

/**
 * Export `SocialIcon` styled component.
 */

export const SocialIcon = styled.a`
  color: #9ca3af;
  font-size: 24px;
  transition: color 0.3s ease;

  &:hover {
    color: white;
  }
`;

/**
 * Export `QuickLinksList` styled component.
 */

export const QuickLinksList = styled.ul`
  list-style: none;
  padding: 0;

  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    margin: 0;
  }

  @media (max-width: 576px) {
    display: block;
    margin: 0;
    justify-content: initial;
  }

  @media (max-width: 480px) {
    display: flex;
  }
`;

/**
 * Export `QuickLink` styled component.
 */

export const QuickLink = styled.li`
  margin-bottom: 8px;

  button {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 14px;
    cursor: pointer;
    transition: color 0.3s ease;

    &:hover {
      color: white;
    }
  }

  @media (max-width: 768px) {
    margin: 12px;
  }

  @media (max-width: 576px) {
    margin: 0;
    margin-bottom: 8px;
  }

  @media (max-width: 480px) {
    margin: 0;
    margin-right: 16px;
  }
`;

/**
 * Export `FooterTitle` styled component.
 */

export const FooterTitle = styled(SubTitle)`
  padding-bottom: 18px;

  @media (max-width: 480px) {
    padding-bottom: 0;
  }
`;

/**
 * Export `FooterQuickLinksTitle` styled component.
 */

export const FooterQuickLinksTitle = styled(FooterTitle)`
  @media (max-width: 768px) {
    text-align: center;
    padding-bottom: 0;
  }

  @media (max-width: 576px) {
    text-align: initial;
    padding-bottom: 8px;
  }
`;

/**
 * Export `Divider` styled component.
 */

export const Divider = styled.hr`
  border-color: #1f2937;
  margin: 32px 0;
`;

/**
 * Export `Copyright` styled component.
 */

export const Copyright = styled.div`
  margin-top: 60px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

/**
 * Export `FooterLink` styled component.
 */

export const FooterLink = styled(Link)`
  color: #9ca3af;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Export `Creator` styled component.
 */

export const Creator = styled.p`
  font-size: 12px;
  margin: 0 auto;
  text-align: center;
  margin-top: 8px;
  color: ${color('gray600')};
  transition: color 0.3s ease;

  &:hover {
    color: ${color('gray500')};
  }
`;
